import { useCallback, useEffect, useState } from 'react';
import { Confirm, type Destructive } from './Confirm.tsx';

/**
 * My plan, on the website — docs/32.
 *
 * What the person has, where it was bought (and so where it is managed),
 * when it renews or ends, a Cancel button for a plan bought on the website,
 * and every invoice and credit note, each a printable page.
 *
 * A plan bought in the App Store or Google Play is shown, never cancelled
 * here: only Apple or Google can stop their own subscriptions, and saying so
 * is more useful than a button that cannot work.
 */

export interface BillingTransport {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
}

interface Me {
  tier: 'free' | 'plus' | 'family' | 'org';
  plan: {
    tier: string;
    status: string;
    store: string | null;
    boughtHere: boolean;
    where: string;
    currentPeriodEnd: number | null;
    cancelAtPeriodEnd: boolean;
    canCancel: boolean;
  } | null;
  invoices: Array<{
    id: string;
    number: string;
    kind: 'invoice' | 'credit_note';
    issuedAt: number;
    description: string;
    currency: string;
    grossMinor: number;
    seller: string;
  }>;
}

const TIER: Record<string, string> = { plus: 'DrBell Plus', family: 'DrBell Family', org: 'DrBell for organisations', free: 'Free' };

function money(currency: string, minor: number): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(minor / 100);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
}

const day = (seconds: number | null) =>
  seconds ? new Date(seconds * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : null;

export function BillingPanel({
  transport,
  refreshKey = 0,
  apiBase = '',
  onPlan,
}: {
  transport: BillingTransport;
  /** Change it to reload, e.g. after a purchase. */
  refreshKey?: number;
  /** Prefix for invoice links when the API is not same-origin. */
  apiBase?: string;
  /** Told whether the person has a paid plan, so the page can hide the plan chooser. */
  onPlan?: (hasPlan: boolean) => void;
}) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Destructive | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await transport.get<Me>('/api/billing/web/me');
      setMe(next);
      onPlan?.(next.plan !== null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [transport, onPlan]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (error && !me) return <p role="alert" className="text-sm text-red-600">{error}</p>;
  if (!me) return <p className="text-sm text-(--muted)">Loading your plan…</p>;

  const plan = me.plan;
  const ends = day(plan?.currentPeriodEnd ?? null);

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-(--line) bg-(--panel) p-4">
        <h2 className="text-base font-semibold">Your plan</h2>
        {!plan ? (
          <p className="mt-1 text-sm text-(--muted)">
            Free. Unlimited medicines and reminders that keep ringing cost nothing; a paid plan adds more people and more
            ways to reach them.
          </p>
        ) : (
          <div className="mt-1 space-y-1 text-sm">
            <p>
              <strong>{TIER[plan.tier] ?? plan.tier}</strong>
              {plan.status === 'past_due' ? ' — the last payment did not go through; it is being retried' : ''}
            </p>
            {ends && (
              <p className="text-(--muted)">
                {plan.cancelAtPeriodEnd ? `Cancelled: you keep it until ${ends}, then the free plan.` : `Renews on ${ends}.`}
              </p>
            )}
            <p className="text-(--muted)">
              {plan.boughtHere ? 'Bought here, on the website, so it is managed here.' : plan.where}
            </p>
            {plan.canCancel && (
              <button
                type="button"
                onClick={() =>
                  setConfirm({
                    title: 'Cancel your plan?',
                    body: `It will not renew${ends ? ` on ${ends}` : ''}. You keep everything until then.`,
                    reassurance: 'Afterwards you are on the free plan: every medicine and every reminder keeps working.',
                    confirmLabel: 'Cancel plan',
                    onConfirm: async () => {
                      await transport.post('/api/billing/web/cancel', {});
                      setConfirm(null);
                      await load();
                    },
                  })
                }
                className="mt-2 rounded-lg border border-(--line) px-3 py-1.5 text-sm hover:bg-(--bg)"
              >
                Cancel plan
              </button>
            )}
          </div>
        )}
      </section>

      {me.invoices.length > 0 && (
        <section className="rounded-2xl border border-(--line) bg-(--panel) p-4">
          <h2 className="text-base font-semibold">Invoices</h2>
          <ul className="mt-2 divide-y divide-(--line) text-sm">
            {me.invoices.map((inv) => (
              <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span>
                  <span className="font-medium">{inv.kind === 'credit_note' ? 'Credit note' : 'Invoice'} {inv.number}</span>
                  <span className="block text-xs text-(--muted)">
                    {new Date(inv.issuedAt).toLocaleDateString()} · {inv.seller}
                  </span>
                </span>
                <span className="flex items-center gap-3">
                  <span>{money(inv.currency, inv.grossMinor)}</span>
                  <a
                    className="underline"
                    href={`${apiBase}/api/billing/web/invoices/${encodeURIComponent(inv.id)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View / print
                  </a>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {confirm && <Confirm {...confirm} onCancel={() => setConfirm(null)} />}
    </div>
  );
}
